import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft, ShoppingCart, Grid, X, ChevronRight,
  ChevronDown, ChevronUp, MapPin, Package,
  Minus, Plus, SlidersHorizontal, Image as ImageIcon, ArrowRight,
} from "lucide-react";

import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { fetchAllCommercePartners, listenToCommerceData } from "../../redux/commercePartnersSlice";
import { listenToBrandProducts, stopBrandProductsListener } from "../../redux/classifiedsSlice";
import { addToCart, decrementCartItem } from "../../redux/cartSlice";

import {
  electronicsCategories,
  electronicsSubcategories,
  electronicsProducts,
} from "../CATEGORIES/ElectronicsCategories";

/* ═══════════════════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════════════════ */
const T = {
  bg:       "#FFFFFF",
  bgPage:   "#F7F8FC",
  bgMuted:  "#EEF2FF",
  border:   "#E4E9F5",
  muted:    "#8A95B0",
  ink:      "#0D1025",
  blue:     "#0A66C2",
  blueDim:  "rgba(10,102,194,0.08)",
  red:      "#E50914",
  gold:     "#FEBA02",
  green:    "#16A34A",
  grad:     "linear-gradient(135deg,#0A66C2 0%,#003580 100%)",
  gradH:    "linear-gradient(135deg,#0A66C2 0%,#000C24 100%)",
};

const fmtEur = (val: number) =>
  `€${val.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const getInitials = (name: string) =>
  (name ?? "?").split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase() ?? "").join("");

/* ═══════════════════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════════════════ */
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,400;0,700;0,900;1,400;1,700&family=DM+Sans:wght@300;400;500;600;700;800&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: #F0F4FA; }
  ::-webkit-scrollbar-thumb { background: #C8D0E0; border-radius: 4px; }
  input, button { font-family: inherit; }

  @keyframes msh-fade-in { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
  @keyframes msh-ping    { 0% { transform:scale(1); opacity:0.8; } 100% { transform:scale(2.2); opacity:0; } }
  @keyframes msh-skeleton {
    0%   { background-position: -200% 0; }
    100% { background-position:  200% 0; }
  }

  .msh-card {
    animation: msh-fade-in 0.35s ease both;
    transition: transform 0.25s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.25s ease;
  }
  .msh-card:hover { transform: translateY(-3px); box-shadow: 0 16px 40px rgba(10,102,194,0.14) !important; }

  .msh-row:hover  { background: #F0F4FF !important; }
  .msh-prod:hover { background: #EFF3FF !important; color: #0A66C2 !important; }
  .msh-circle:hover { transform: translateY(-2px); }

  .msh-skeleton {
    background: linear-gradient(90deg,#F0F4FA 25%,#E4E9F5 50%,#F0F4FA 75%);
    background-size: 200% 100%;
    animation: msh-skeleton 1.4s ease-in-out infinite;
    border-radius: 12px;
  }
`;

/* ═══════════════════════════════════════════════════════
   PRODUCT CARD
═══════════════════════════════════════════════════════ */
const ProductCard: React.FC<{
  item: any; index: number; qty: number;
  onAdd: () => void; onDec: () => void; onPress: () => void;
}> = ({ item, index, qty, onAdd, onDec, onPress }) => {
  const photo       = item.photos?.[0];
  const discount    = typeof item.discount === "number" && item.discount > 0 ? item.discount : null;
  const hasVariants = item.variants && Object.keys(item.variants).length > 0;

  return (
    <motion.div
      className="msh-card"
      style={{ animationDelay: `${Math.min(index * 0.05, 0.5)}s`, borderRadius: 18, background: "#fff", border: `1.5px solid ${T.border}`, boxShadow: "0 4px 16px rgba(10,102,194,0.07)", cursor: "pointer", overflow: "hidden", display: "flex", flexDirection: "column" }}
      onClick={onPress}
      whileTap={{ scale: 0.97 }}
    >
      {/* image */}
      <div style={{ position: "relative", aspectRatio: "1/1", background: "#F0F4FF", overflow: "hidden" }}>
        {photo ? (
          <img src={photo} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "contain", transition: "transform 0.3s ease" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLImageElement).style.transform = "scale(1.05)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLImageElement).style.transform = "scale(1)"; }} />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ImageIcon size={36} color={T.muted} strokeWidth={1.2} />
          </div>
        )}
        {discount !== null && (
          <div style={{ position: "absolute", top: 8, left: 8, background: T.red, color: "#fff", fontSize: 10, fontWeight: 800, padding: "2px 7px", borderRadius: 5, zIndex: 2 }}>
            {discount}% OFF
          </div>
        )}
        <div style={{ position: "absolute", top: 8, right: 8, background: "rgba(255,255,255,0.96)", borderRadius: 8, padding: "3px 8px", boxShadow: "0 2px 8px rgba(0,0,0,0.10)", zIndex: 2 }}>
          <span style={{ fontSize: 11, fontWeight: 900, color: T.blue }}>{fmtEur(item.rate ?? 0)}</span>
        </div>
      </div>

      {/* info */}
      <div style={{ padding: "12px 12px 14px", display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: T.ink, lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden" }}>
          {item.name ?? "Unnamed product"}
        </span>
        {discount !== null && item.originalRate != null && (
          <span style={{ fontSize: 10, color: T.muted, textDecoration: "line-through", fontWeight: 500 }}>
            {fmtEur(item.originalRate)}
          </span>
        )}
        {/* cart controls */}
        <div style={{ marginTop: "auto", paddingTop: 8 }} onClick={(e) => e.stopPropagation()}>
          {hasVariants ? (
            <button onClick={(e) => { e.stopPropagation(); onPress(); }}
              style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 12px", borderRadius: 9, background: T.grad, border: "none", color: "#fff", fontSize: 12, fontWeight: 800, cursor: "pointer" }}>
              <SlidersHorizontal size={12} />Options
            </button>
          ) : qty > 0 ? (
            <div style={{ display: "flex", alignItems: "center", background: T.blue, borderRadius: 9, overflow: "hidden", width: "fit-content" }}>
              <button onClick={(e) => { e.stopPropagation(); onDec(); }}
                style={{ width: 30, height: 30, background: "rgba(255,255,255,0.14)", border: "none", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Minus size={12} color="#fff" />
              </button>
              <span style={{ color: "#fff", fontWeight: 900, minWidth: 26, textAlign: "center", fontSize: 13 }}>{qty}</span>
              <button onClick={(e) => { e.stopPropagation(); onAdd(); }}
                style={{ width: 30, height: 30, background: "rgba(255,255,255,0.18)", border: "none", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Plus size={12} color="#fff" />
              </button>
            </div>
          ) : (
            <button onClick={(e) => { e.stopPropagation(); onAdd(); }}
              style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 12px", borderRadius: 9, background: `linear-gradient(135deg,${T.blue},${T.red})`, border: "none", color: "#fff", fontSize: 12, fontWeight: 800, cursor: "pointer" }}>
              <ShoppingCart size={12} />Add
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════════════ */
const MetaStoreHome: React.FC = () => {
  const dispatch  = useDispatch<AppDispatch>();
  const navigate  = useNavigate();
  const { brandId } = useParams<{ brandId: string }>();

  const brandProducts = useSelector((s: RootState) => s.classifieds.brandListings);
  const brandLoading  = useSelector((s: RootState) => s.classifieds.brandListingsLoading);
  const partners      = useSelector((s: RootState) => (s as any).commercePartners?.partners ?? []);
  const cartItems     = useSelector((s: RootState) => s.cart.items);
  const userId        = useSelector((s: RootState) => s.auth.user?.userId);

  const cartTotal = cartItems.reduce((s: number, i: any) => s + i.qty, 0);
  const cartPrice = cartItems.reduce((s: number, i: any) => s + i.qty * i.price, 0);
  const brand     = useMemo(() => partners.find((p: any) => p.brandId === brandId) ?? null, [partners, brandId]);

  const [isMobile,       setIsMobile]       = useState(window.innerWidth <= 900);
  const [drawerOpen,     setDrawerOpen]     = useState(false);
  const [openCat,        setOpenCat]        = useState<number | null>(null);
  const [openSub,        setOpenSub]        = useState<number | null>(null);
  const [selectedCatId,  setSelectedCatId]  = useState<number | null>(null);
  const [selectedSubId,  setSelectedSubId]  = useState<number | null>(null);
  const [selectedProdId, setSelectedProdId] = useState<number | null>(null);
  const [scrolled,       setScrolled]       = useState(false);

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 900);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 180);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    if (!brandId) return;
    dispatch(fetchAllCommercePartners() as any);
    const unsub = dispatch(listenToCommerceData(brandId) as any);
    dispatch(listenToBrandProducts(brandId) as any);
    return () => { if (typeof unsub === "function") unsub(); dispatch(stopBrandProductsListener() as any); };
  }, [dispatch, brandId]);

  /* visible IDs */
  const visibleIds = useMemo(() => {
    const cats = new Set<number>(), subs = new Set<number>(), prods = new Set<number>();
    brandProducts.forEach((p: any) => {
      if (p.categoryId)    cats.add(p.categoryId);
      if (p.subcategoryId) subs.add(p.subcategoryId);
      if (p.productId)     prods.add(p.productId);
    });
    return { cats, subs, prods };
  }, [brandProducts]);

  /* only the electronics categories this store actually has */
  const visibleCats = useMemo(
    () => (electronicsCategories as any[]).filter((c) => visibleIds.cats.has(c.id)),
    [visibleIds]
  );

  /* filtered products */
  const filtered = useMemo(() => {
    let r = brandProducts as any[];
    if (selectedCatId  !== null) r = r.filter((p: any) => p.categoryId    === selectedCatId);
    if (selectedSubId  !== null) r = r.filter((p: any) => p.subcategoryId === selectedSubId);
    if (selectedProdId !== null) r = r.filter((p: any) => p.productId === selectedProdId || p.productVariantId === selectedProdId);
    return r;
  }, [brandProducts, selectedCatId, selectedSubId, selectedProdId]);

  const hasFilter = selectedCatId !== null || selectedSubId !== null || selectedProdId !== null;

  const resetFilters = () => { setSelectedCatId(null); setSelectedSubId(null); setSelectedProdId(null); setOpenCat(null); setOpenSub(null); };

  const getQty    = (id: string) => cartItems.find((i: any) => i.listingId === id)?.qty ?? 0;
  const handleAdd = (p: any) => dispatch(addToCart({ listingId: p.listingId, name: p.name ?? "", price: p.rate ?? 0, photo: p.photos?.[0], hostId: p.hostId, brandId: p.brandId }));
  const handleDec = (id: string) => dispatch(decrementCartItem({ listingId: id }));

  const goToCheckout = () => {
    if (!cartItems.length) return;
    navigate("/MetaCommerceCheckout", { state: { userId, totalQty: cartTotal, totalPrice: cartPrice, items: cartItems } });
  };

  /* section label */
  const sectionLabel = useMemo(() => {
    if (selectedProdId !== null) {
      const p = (electronicsProducts as any[]).find((x: any) => x.id === selectedProdId);
      if (p) return p.name;
    }
    if (selectedSubId !== null) {
      const s = (electronicsSubcategories as any[]).find((x: any) => x.id === selectedSubId);
      if (s) return s.name;
    }
    if (selectedCatId !== null) {
      const c = (electronicsCategories as any[]).find((x: any) => x.id === selectedCatId);
      if (c) return c.name;
    }
    return "All Products";
  }, [selectedCatId, selectedSubId, selectedProdId]);

  /* ── CATEGORY TREE (shared by drawer + sidebar) ──── */
  const renderTree = (closeOnLeaf: boolean) =>
    visibleCats.map((cat: any) => {
      const isCatOpen = openCat === cat.id;
      const CatIcon   = cat.icon as any;
      const visibleSubs = (electronicsSubcategories as any[]).filter((s: any) => s.parentId === cat.id && visibleIds.subs.has(s.id));
      return (
        <div key={cat.id}>
          <button className="msh-row"
            onClick={() => { setOpenCat(isCatOpen ? null : cat.id); setOpenSub(null); setSelectedCatId(cat.id); setSelectedSubId(null); setSelectedProdId(null); }}
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "11px 18px", background: selectedCatId === cat.id ? T.blueDim : "transparent", border: "none", width: "100%", cursor: "pointer", fontSize: 13, fontWeight: 700, color: T.ink }}>
            {CatIcon && <CatIcon size={14} color={T.blue} />}
            <span style={{ flex: 1, textAlign: "left" }}>{cat.name}</span>
            {isCatOpen ? <ChevronUp size={12} color="#bbb" /> : <ChevronDown size={12} color="#bbb" />}
          </button>

          <AnimatePresence>
            {isCatOpen && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} style={{ overflow: "hidden" }}>
                {visibleSubs.map((sub: any) => {
                  const isSubOpen = openSub === sub.id;
                  const SubIcon   = sub.icon as any;
                  const visibleProds = (electronicsProducts as any[]).filter((p: any) => p.parentId === sub.id && visibleIds.prods.has(p.id));
                  return (
                    <div key={sub.id} style={{ paddingLeft: 14 }}>
                      <button className="msh-row"
                        onClick={() => { setOpenSub(isSubOpen ? null : sub.id); setSelectedCatId(cat.id); setSelectedSubId(sub.id); setSelectedProdId(null); }}
                        style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 18px", background: selectedSubId === sub.id ? T.blueDim : "transparent", border: "none", width: "100%", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#444" }}>
                        {SubIcon && <SubIcon size={13} color={T.blue} />}
                        <span style={{ flex: 1, textAlign: "left" }}>{sub.name}</span>
                        {isSubOpen ? <ChevronUp size={11} color="#ccc" /> : <ChevronDown size={11} color="#ccc" />}
                      </button>

                      <AnimatePresence>
                        {isSubOpen && (
                          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.15 }} style={{ overflow: "hidden" }}>
                            {visibleProds.map((prod: any) => {
                              const PIcon = prod.icon as any;
                              return (
                                <button key={prod.id} className="msh-prod"
                                  onClick={() => { setSelectedCatId(cat.id); setSelectedSubId(sub.id); setSelectedProdId(prod.id); if (closeOnLeaf) setDrawerOpen(false); }}
                                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 18px 8px 30px", background: selectedProdId === prod.id ? T.blueDim : "transparent", border: "none", width: "100%", cursor: "pointer", fontSize: 11.5, fontWeight: 500, color: selectedProdId === prod.id ? T.blue : "#777" }}>
                                  {PIcon && <PIcon size={11} color="#aaa" />}
                                  <span style={{ textAlign: "left" }}>{prod.name}</span>
                                </button>
                              );
                            })}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
          <div style={{ height: 1, background: "#F0F4FA", marginInline: 12 }} />
        </div>
      );
    });

  /* ── STICKY HEADER ────────────────────────────────── */
  const StickyHeader = () => (
    <AnimatePresence>
      {scrolled && (
        <motion.div
          initial={{ y: -56, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -56, opacity: 0 }}
          transition={{ type: "spring", stiffness: 420, damping: 32 }}
          style={{ position: "fixed", top: 0, left: 0, right: 0, height: 56, background: T.gradH, display: "flex", alignItems: "center", gap: 14, paddingInline: 20, zIndex: 300, boxShadow: "0 4px 20px rgba(0,0,0,0.28)" }}
        >
          <button onClick={() => navigate(-1)} style={{ width: 34, height: 34, borderRadius: "50%", background: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.22)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff" }}>
            <ChevronLeft size={18} />
          </button>
          <span style={{ fontFamily: "'Fraunces',serif", fontWeight: 700, fontSize: 16, color: "#fff", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {brand?.name ?? "Store"}
          </span>
          <button onClick={goToCheckout} style={{ position: "relative", width: 34, height: 34, borderRadius: "50%", background: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.22)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff" }}>
            <ShoppingCart size={17} />
            {cartTotal > 0 && <span style={{ position: "absolute", top: -4, right: -4, background: T.red, color: "#fff", fontSize: 8, fontWeight: 900, width: 15, height: 15, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{cartTotal > 99 ? "99+" : cartTotal}</span>}
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );

  /* ── HERO ─────────────────────────────────────────── */
  const Hero = () => (
    <div style={{ position: "relative", height: 240, overflow: "hidden", background: T.gradH }}>
      <div style={{ position: "absolute", top: -60, right: -60, width: 220, height: 220, borderRadius: "50%", background: "rgba(254,186,2,0.10)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: 10, left: -60, width: 180, height: 180, borderRadius: "50%", background: "rgba(255,255,255,0.04)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", top: 14, left: 14, right: 14, display: "flex", alignItems: "center", zIndex: 10 }}>
        <button onClick={() => navigate(-1)} style={{ width: 40, height: 40, borderRadius: "50%", background: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.22)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff" }}>
          <ChevronLeft size={20} />
        </button>
        <div style={{ flex: 1 }} />
        <button onClick={goToCheckout} style={{ position: "relative", width: 40, height: 40, borderRadius: "50%", background: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.22)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff" }}>
          <ShoppingCart size={18} />
          {cartTotal > 0 && <span style={{ position: "absolute", top: -4, right: -4, background: T.red, color: "#fff", fontSize: 8, fontWeight: 900, width: 16, height: 16, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{cartTotal > 99 ? "99+" : cartTotal}</span>}
        </button>
      </div>
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, display: "flex", flexDirection: "column", alignItems: "center", paddingBottom: 22 }}>
        {brand?.image
          ? <img src={brand.image} alt={brand.name} style={{ width: 72, height: 72, borderRadius: 20, objectFit: "cover", border: "3px solid rgba(255,255,255,0.25)", marginBottom: 10 }} />
          : <div style={{ width: 72, height: 72, borderRadius: 20, background: "rgba(255,255,255,0.14)", border: "3px solid rgba(255,255,255,0.25)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10 }}>
              <span style={{ fontFamily: "'Fraunces',serif", fontSize: 26, fontWeight: 900, color: "#fff" }}>{getInitials(brand?.name ?? "")}</span>
            </div>
        }
        <h1 style={{ fontFamily: "'Fraunces',serif", fontSize: 22, fontWeight: 900, color: "#fff", letterSpacing: 0.2, marginBottom: 8, textShadow: "0 2px 8px rgba(0,0,0,0.4)" }}>
          {brand?.name ?? "Loading…"}
        </h1>
        <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
          {brand?.address?.city && (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <MapPin size={13} color={T.red} />
              <span style={{ fontSize: 12, color: "rgba(255,255,255,0.75)", fontWeight: 600 }}>{[brand.address.city, brand.address.country].filter(Boolean).join(", ")}</span>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Package size={12} color="rgba(255,255,255,0.7)" />
            <span style={{ fontSize: 12, color: "rgba(255,255,255,0.75)", fontWeight: 600 }}>
              {brandLoading ? "Loading…" : `${brandProducts.length} product${brandProducts.length !== 1 ? "s" : ""}`}
            </span>
          </div>
          {brand?.isPublished && (
            <div style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(10,102,194,0.28)", borderRadius: 999, padding: "4px 12px", border: "1px solid rgba(10,102,194,0.5)" }}>
              <span style={{ position: "relative", width: 7, height: 7, flexShrink: 0 }}>
                <span style={{ display: "block", width: 7, height: 7, borderRadius: "50%", background: T.gold }} />
                <span style={{ position: "absolute", inset: 0, borderRadius: "50%", background: T.gold, animation: "msh-ping 2s ease-out infinite" }} />
              </span>
              <span style={{ color: T.gold, fontWeight: 800, fontSize: 11 }}>Open</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  /* ── MOBILE CIRCLES BAR ───────────────────────────── */
  const CirclesBar = () => {
    if (!visibleCats.length) return null;
    return (
      <div style={{ background: "#fff", borderBottom: `1px solid ${T.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px 6px" }}>
          <button onClick={() => setDrawerOpen(true)} style={{ display: "flex", alignItems: "center", gap: 5, border: `1.5px solid ${T.blue}`, borderRadius: 8, padding: "6px 12px", background: "#fff", color: T.blue, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            <Grid size={13} />Categories
          </button>
          {hasFilter && (
            <button onClick={resetFilters} style={{ display: "flex", alignItems: "center", gap: 4, border: `1.5px solid ${T.gold}`, borderRadius: 8, padding: "5px 10px", background: "#FFFBEA", color: "#A07800", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
              <X size={11} />Clear
            </button>
          )}
        </div>
        <div style={{ display: "flex", overflowX: "auto", scrollbarWidth: "none", padding: "8px 12px 12px", gap: 0 }}>
          {visibleCats.map((cat: any) => {
            const isSelected = selectedCatId === cat.id;
            const CatIcon = cat.icon as any;
            return (
              <button key={cat.id} className="msh-circle"
                onClick={() => { if (isSelected) { resetFilters(); return; } setSelectedCatId(cat.id); setSelectedSubId(null); setSelectedProdId(null); }}
                style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, minWidth: 62, cursor: "pointer", background: "none", border: "none", padding: "0 4px", transition: "transform 0.2s ease" }}>
                <div style={{ width: 48, height: 48, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", border: `2.5px solid ${isSelected ? T.gold : "#E8ECF5"}`, background: isSelected ? "#EFF4FF" : "#F5F7FB", transition: "all 0.2s ease" }}>
                  {CatIcon ? <CatIcon size={22} color={isSelected ? T.blue : "#777"} /> : <Grid size={20} color={isSelected ? T.blue : "#888"} />}
                </div>
                <span style={{ fontSize: 9, fontWeight: isSelected ? 800 : 600, color: isSelected ? T.blue : "#666", textAlign: "center", lineHeight: 1.2, maxWidth: 54, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {cat.name}
                </span>
                {isSelected && <div style={{ height: 2, width: 28, borderRadius: 1, background: T.gold }} />}
              </button>
            );
          })}
        </div>
        <div style={{ height: 1, background: T.border }} />
      </div>
    );
  };

  /* ── DRAWER ──────────────────────────────────────── */
  const Drawer = () => (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setDrawerOpen(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(13,16,37,0.44)", zIndex: 400, backdropFilter: "blur(3px)" }} />
          <motion.div
            initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
            transition={{ type: "spring", stiffness: 360, damping: 32 }}
            style={{ position: "fixed", top: 0, left: 0, bottom: 0, width: Math.min(320, window.innerWidth * 0.88), background: "#fff", zIndex: 401, display: "flex", flexDirection: "column", boxShadow: "8px 0 40px rgba(0,0,0,0.18)" }}>
            <div style={{ background: T.gradH, padding: "20px 18px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
              <div>
                <div style={{ fontFamily: "'Fraunces',serif", fontWeight: 900, fontSize: 18, color: "#fff" }}>Electronics</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.65)", fontWeight: 600, marginTop: 2 }}>Browse categories</div>
              </div>
              <button onClick={() => setDrawerOpen(false)} style={{ width: 34, height: 34, borderRadius: "50%", background: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.22)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff" }}>
                <X size={16} />
              </button>
            </div>
            {hasFilter && (
              <div style={{ padding: "10px 16px", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
                <button onClick={() => { resetFilters(); setDrawerOpen(false); }} style={{ display: "flex", alignItems: "center", gap: 5, border: `1.5px solid ${T.gold}`, borderRadius: 8, padding: "7px 14px", background: "#FFFBEA", color: "#A07800", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                  <X size={12} />Clear all filters
                </button>
              </div>
            )}
            <div style={{ flex: 1, overflowY: "auto", paddingBottom: 32 }}>
              {renderTree(true)}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  /* ── DESKTOP SIDEBAR ─────────────────────────────── */
  const Sidebar = () => {
    if (!visibleCats.length) return null;
    return (
      <div style={{ width: 248, flexShrink: 0, position: "sticky", top: 76, alignSelf: "flex-start", maxHeight: "calc(100vh - 90px)", overflowY: "auto", background: "#fff", borderRadius: 18, border: `1px solid ${T.border}`, boxShadow: "0 4px 20px rgba(10,102,194,0.06)" }}>
        <div style={{ background: T.gradH, padding: "16px 16px 14px", borderRadius: "18px 18px 0 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontFamily: "'Fraunces',serif", fontWeight: 900, fontSize: 16, color: "#fff" }}>Electronics</span>
          {hasFilter && (
            <button onClick={resetFilters} style={{ display: "flex", alignItems: "center", gap: 4, background: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.22)", borderRadius: 6, padding: "4px 10px", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
              <X size={10} />Clear
            </button>
          )}
        </div>
        {renderTree(false)}
      </div>
    );
  };

  /* ── PRODUCT GRID ────────────────────────────────── */
  const ProductGrid = () => {
    if (brandLoading && !filtered.length) {
      return (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(auto-fill,minmax(200px,1fr))", gap: 16 }}>
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="msh-skeleton" style={{ height: 280 }} />)}
        </div>
      );
    }
    if (!filtered.length) {
      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "70px 40px", gap: 14, textAlign: "center" }}>
          <div style={{ width: 80, height: 80, borderRadius: 24, background: "#EEF2FF", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8 }}>
            <Package size={36} color={T.muted} strokeWidth={1.2} />
          </div>
          <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 19, fontWeight: 900, color: T.ink }}>
            {brandLoading ? "Loading products…" : "No products here yet"}
          </h3>
          <p style={{ fontSize: 13, color: T.muted, lineHeight: 1.6 }}>
            {hasFilter && !brandLoading ? "Nothing matches this filter." : brandLoading ? "" : "Check back soon for new arrivals."}
          </p>
          {hasFilter && !brandLoading && (
            <button onClick={resetFilters} style={{ padding: "10px 24px", borderRadius: 999, background: T.grad, border: "none", color: "#fff", fontWeight: 800, fontSize: 13, cursor: "pointer" }}>
              Clear filters
            </button>
          )}
        </div>
      );
    }
    return (
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(auto-fill,minmax(200px,1fr))", gap: 16 }}>
        {filtered.map((product: any, index: number) => (
          <ProductCard
            key={product.listingId}
            item={product}
            index={index}
            qty={getQty(product.listingId)}
            onAdd={() => handleAdd(product)}
            onDec={() => handleDec(product.listingId)}
            onPress={() => navigate(`/MetaCommerceDetails/${product.listingId}`)}
          />
        ))}
      </div>
    );
  };

  /* ── CART BAR ─────────────────────────────────────── */
  const CartBar = () => (
    <AnimatePresence>
      {cartTotal > 0 && (
        <motion.div
          initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
          transition={{ type: "spring", stiffness: 380, damping: 28 }}
          style={{ position: "fixed", bottom: 0, left: 0, right: 0, padding: "12px 20px 20px", zIndex: 200, background: "linear-gradient(to top,#fff 70%,transparent)" }}
        >
          <button onClick={goToCheckout}
            style={{ width: "100%", maxWidth: 600, margin: "0 auto", display: "flex", alignItems: "center", padding: "14px 20px", borderRadius: 16, background: T.gradH, border: "none", cursor: "pointer", boxShadow: "0 8px 32px rgba(10,102,194,0.40)" }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: T.gold, display: "flex", alignItems: "center", justifyContent: "center", marginRight: 12, flexShrink: 0 }}>
              <span style={{ color: T.blue, fontWeight: 900, fontSize: 12 }}>{cartTotal}</span>
            </div>
            <span style={{ color: "#fff", fontWeight: 800, fontSize: 15, flex: 1, textAlign: "left", fontFamily: "'DM Sans',sans-serif" }}>View Cart</span>
            <span style={{ color: T.gold, fontWeight: 900, fontSize: 15, marginRight: 8, fontFamily: "'DM Sans',sans-serif" }}>{fmtEur(cartPrice)}</span>
            <ArrowRight size={18} color={T.gold} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );

  /* ─────────────────────────────────────────────────── */
  return (
    <div style={{ minHeight: "100vh", background: T.bgPage, fontFamily: "'DM Sans',sans-serif", paddingBottom: cartTotal > 0 ? 90 : 40 }}>
      <style>{GLOBAL_CSS}</style>
      <StickyHeader />
      <Hero />
      {isMobile && <CirclesBar />}
      <Drawer />

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: isMobile ? "20px 14px" : "28px 28px" }}>
        {/* section header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 4, height: 22, borderRadius: 2, background: T.grad }} />
            <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 18, fontWeight: 700, color: T.ink }}>{sectionLabel}</h2>
            <span style={{ fontSize: 12, fontWeight: 700, color: T.muted, background: "#EEF2FF", padding: "2px 9px", borderRadius: 20 }}>{filtered.length}</span>
          </div>
          {hasFilter && (
            <button onClick={resetFilters} style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 8, background: "#FFFBEA", border: `1px solid ${T.gold}`, color: "#A07800", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              <X size={11} />All products
            </button>
          )}
        </div>

        {!isMobile ? (
          <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
            <Sidebar />
            <div style={{ flex: 1 }}><ProductGrid /></div>
          </div>
        ) : (
          <ProductGrid />
        )}
      </div>

      <CartBar />
    </div>
  );
};

export default MetaStoreHome;